import { useEffect, useRef, useState } from "react";
import { propSrc, propSpec, type PropId } from "./props";
import { stock } from "./shelves";
import { capture, type SeasonLayout } from "./arrangement";
import { LayoutsShelf, SignIn } from "./LayoutsShelf";
import { VISIT_RANGES, VisitorsShelf } from "./VisitorsShelf";
import { SEASON_NAMES, type Season } from "./seasons";
import { CLOTHES, WARDROBE_SHELVES, closetSrc, garment } from "./clothes";
import { floralSrc, paletteOf, useFloral } from "./florals";
import { RacksShelf } from "./RacksShelf";
import { SeasonsShelf, SEASON_EMOJI, titleCase } from "./SeasonsShelf";
import { closetMovesNow, freshFromCode, hiddenChanged, movesChanged, publishRacks, racksChanged, resetRacks, useRackOrder, type Which } from "./closetRacks";
import { resizeSticker } from "./Draggable";
import { saveSeasonDefault, type Device } from "./layoutsDb";
import { defaultLook } from "./roomLayouts";
import { PLACE_NAMES, type Place } from "./place";
import { differs } from "./roomLayouts";
import { messageOf, type Collection } from "./useCollection";
import { addedBy, isLocked, withoutOthers } from "./placements";

const EMPTY_LIST: string[] = [];

/** the season after this one, for the plaque's "skip" label */
const nextSeason = (s: Season) => SEASON_NAMES[(SEASON_NAMES.indexOf(s) + 1) % SEASON_NAMES.length];

/** the one account that sees the visitor book, and whose Done saves the room */
const OWNER = "mollyjbeach@gmail.com";

/**
 * The room's catalogue, like the wardrobe screen in a dress-up game: every
 * sticker on a shelf, with a tick to put it in the room or take it out, and a
 * tick on each shelf for the whole lot at once. It's the same in every room on
 * the map, and it says which room and season it's dressing.
 *
 * It reads what's in the room off the page when it opens, so the phone layout
 * and the desktop layout each list exactly their own stickers.
 *
 * Its other pages:
 * - Seasons (SeasonsShelf.tsx): the room's eight layouts, a season each on a
 *   computer and a phone, and what's in each.
 * - Saved looks (LayoutsShelf.tsx): whole arrangements of the room, kept.
 * - In the Wardrobe Wing, Clothes (clothes.ts, a shelf per kind of thing) and
 *   Racks (RacksShelf.tsx), for arranging them on the closet's rails.
 *
 * Signed in, Molly's Done saves the room as its layout for the season it's in,
 * on this device, and the closet's rails as they are.
 */

type Page = "clothes" | "racks" | "stickers" | "seasons" | "looks" | "visitors";

const nice = (id: string) => {
  let label: string = id;
  try {
    label = propSpec(id as PropId).label;
  } catch {
    /* not in the catalogue of images: fall back to its id */
  }
  return label.charAt(0).toUpperCase() + label.slice(1);
};

function ShelfTick({ all, some, onChange, label }: { all: boolean; some: boolean; onChange: () => void; label: string }) {
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current) box.current.indeterminate = some && !all;
  }, [all, some]);
  return (
    <label className="cat-shelf-tick">
      <input ref={box} type="checkbox" checked={all} onChange={onChange} aria-label={label} />
      <span aria-hidden className="cat-heart" />
    </label>
  );
}

/** the closet's clothes, sorted onto a shelf per kind of thing */
function wardrobeStock(inCloset: string[]) {
  return WARDROBE_SHELVES.map((shelf) => ({
    shelf,
    items: inCloset.filter((id) => garment(id)?.kind === shelf.key),
  })).filter((s) => s.items.length > 0);
}

const idsIn = (stage: Element | null | undefined, selector: string) =>
  Array.from(stage?.querySelectorAll<HTMLElement>(selector) ?? [])
    .map((el) => el.dataset.prop!)
    .filter((id, i, all) => all.indexOf(id) === i);

export function Catalogue({
  open,
  onClose,
  place,
  device,
  hidden,
  setHidden,
  closetHidden,
  setClosetHidden,
  collection,
  season,
  wearing,
  onWear,
  onPutIn,
  onSkipSeason,
  roomClothes,
  onRoomClothes,
}: {
  open: boolean;
  onClose: () => void;
  place: Place;
  device: Device;
  /** the stickers taken out of the room */
  hidden: Set<string>;
  setHidden: (next: Set<string>) => void;
  /** the clothes taken out of the closet */
  closetHidden: Set<string>;
  setClosetHidden: (next: Set<string>) => void;
  collection: Collection;
  season: Season;
  /** the layout the room has on, for this device */
  wearing: SeasonLayout | undefined;
  onWear: (layout: SeasonLayout) => void;
  /** put stickers into the room as it is now */
  onPutIn: (ids: string[]) => void;
  /** on to the next season, the way the header's Skip pill does it */
  onSkipSeason?: () => void;
  /** clothes brought out of the wardrobe into this room (RoomClothes.tsx) */
  roomClothes?: string[];
  onRoomClothes?: (ids: string[]) => void;
}) {
  const wardrobe = place === "closet";
  const panel = useRef<HTMLDivElement>(null);
  const [stickers, setStickers] = useState<string[]>([]);
  const [clothes, setClothes] = useState<string[]>([]);
  const [tab, setTab] = useState<string | null>(null);
  const [find, setFind] = useState("");
  // the visitor book's stretch of time, so its buttons can live in the head
  const [visitDays, setVisitDays] = useState(30);
  // and which season's looks the Saved looks page is showing — it follows the
  // room as the year turns, so the page is never talking about a season the
  // room left behind
  const [lookSeason, setLookSeason] = useState<Season>(season);
  useEffect(() => {
    setLookSeason(season);
  }, [season]);
  const floral = useFloral("catalogue");
  // the panel itself is papered in another of the patterns, under a cream veil
  const paper = useFloral("panel");
  // the tabs across the top wear whatever the sidebar is wearing
  const chips = useFloral("sidebar");
  // the title plaque takes the footer's stone, the same as the map's ribbon
  const ribbon = useFloral("footer");
  const palette = paletteOf(floral.now);
  const [copied, setCopied] = useState<"" | "ok" | "fail">("");
  const [page, setPage] = useState<Page>(wardrobe ? "clothes" : "stickers");
  // which of the closet's photographs is showing, for the Racks page
  const [which, setWhich] = useState<Which>("wide");
  const rackOrder = useRackOrder(which);
  const [saving, setSaving] = useState<"" | "busy" | "saved">("");
  const [saveError, setSaveError] = useState("");
  const owner = Boolean(collection.editor?.canSave && collection.editor.email.toLowerCase() === OWNER);
  const phone = device === "phone";
  const stageOf = () => panel.current?.closest<HTMLElement>(".palais-stage") ?? null;

  useEffect(() => {
    if (!open) return;
    const stage = panel.current?.closest(".palais-stage");
    setStickers(idsIn(stage, ".palais-layer [data-prop]"));
    setClothes(idsIn(stage, ".palais-closet [data-prop]"));
    setPage(place === "closet" ? "clothes" : "stickers");
    setFind("");
    setWhich(stage?.querySelector(".palais-closet-frame--tall") ? "tall" : "wide");
    setSaveError("");
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, place]);

  if (!open) return <div ref={panel} hidden />;

  const clothesPage = page === "clothes";
  /* in the Wardrobe Wing the clothes page is the closet itself; anywhere else
     it is the whole wardrobe, to be worn into the room (RoomClothes.tsx) */
  const dressingRoom = clothesPage && !wardrobe && !!onRoomClothes;
  const everyGarment = dressingRoom ? Object.keys(CLOTHES) : EMPTY_LIST;
  const wornHere = new Set(roomClothes ?? EMPTY_LIST);
  const inRoom = dressingRoom ? everyGarment : clothesPage ? clothes : stickers;
  const out = dressingRoom
    ? new Set(everyGarment.filter((id) => !wornHere.has(id)))
    : clothesPage
      ? closetHidden
      : hidden;
  const setOut = dressingRoom
    ? (next: Set<string>) => onRoomClothes!(everyGarment.filter((id) => !next.has(id)))
    : clothesPage
      ? setClosetHidden
      : setHidden;
  const allShelves = dressingRoom
    ? wardrobeStock(everyGarment)
    : clothesPage
      ? wardrobeStock(clothes)
      : stock(stickers);
  const shown = inRoom.filter((id) => !out.has(id)).length;

  /* the search box: every word has to appear somewhere in the piece's name,
     the shop it came from, its shelf, or its id */
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const describe = (id: string, shelfName: string) => {
    const g = clothesPage ? garment(id) : undefined;
    return `${g ? `${g.label} ${g.store} ${g.bought}` : nice(id)} ${shelfName} ${id}`.toLowerCase();
  };
  const shelves = words.length
    ? allShelves
        .map(({ shelf, items }) => ({
          shelf,
          items: items.filter((id) => {
            const text = describe(id, shelf.name);
            return words.every((w) => text.includes(w));
          }),
        }))
        .filter((s) => s.items.length > 0)
    : allShelves;
  const hits = shelves.reduce((n, s) => n + s.items.length, 0);
  /* what the buttons at the bottom work on: the search's matches, or the lot */
  const onPage = words.length ? shelves.flatMap((s) => s.items) : inRoom;

  const setMany = (all: string[], show: boolean) => {
    // what's Molly's is hers to move, and signed out nothing moves (placements.ts)
    const ids = clothesPage ? all : all.filter((id) => !isLocked(id));
    if (!ids.length) return;
    const next = new Set(out);
    for (const id of ids) {
      if (show) next.delete(id);
      else next.add(id);
    }
    setOut(next);
    // one thing put into the room stands in the middle of the view, so it's
    // never lost behind the furniture (justPlaced.ts); a whole shelf at once
    // goes where the room always puts it
    if (show && !clothesPage && ids.length === 1) onPutIn(ids);
  };

  /** make a piece in the room (or the closet) bigger or smaller, like its size handle */
  const nudge = (id: string, factor: number) => {
    if (!clothesPage && isLocked(id)) return;
    const stage = stageOf();
    if (stage) resizeSticker(stage, clothesPage && !dressingRoom ? ".palais-closet [data-prop]" : ".palais-layer [data-prop]", id, factor);
  };
  /** the same, for a whole shelf of pieces, or everything on the page, at once
      (only what's in the room: a piece taken out keeps its size) */
  const nudgeMany = (ids: string[], factor: number) => ids.filter((id) => !out.has(id)).forEach((id) => nudge(id, factor));
  const sizeGroup = (ids: string[], what: string) => {
    const none = !ids.some((id) => !out.has(id));
    return (
      <span className="cat-size cat-size--group" role="group" aria-label={`Size of ${what}`}>
        <button type="button" disabled={none} aria-label={`Make ${what} smaller`} title={`Make ${what} smaller`} onClick={() => nudgeMany(ids, 1 / 1.12)}>
          −
        </button>
        <button type="button" disabled={none} aria-label={`Make ${what} bigger`} title={`Make ${what} bigger`} onClick={() => nudgeMany(ids, 1.12)}>
          +
        </button>
      </span>
    );
  };

  /** the stickers in the room as they are right now, in the form a layout keeps */
  const roomNow = () => {
    const stage = stageOf();
    if (!stage) throw new Error("Couldn't find the room");
    // what other people have put in stays theirs (placements.ts)
    return withoutOthers(capture(stage, hidden));
  };

  /** save the room as it is into one of its season layouts, for this device */
  const saveTo = async (s: Season) => {
    const { stage, props } = roomNow();
    await saveSeasonDefault(collection.saved, {
      place,
      season: s,
      device,
      name: `${PLACE_NAMES[place]} · ${titleCase(s)} · ${phone ? "phone" : "computer"}`,
      stage,
      props,
    });
    await collection.refresh();
  };

  const withSaving = async (job: () => Promise<boolean>, close: boolean) => {
    setSaving("busy");
    setSaveError("");
    try {
      const didSave = await job();
      if (!didSave) {
        setSaving("");
        if (close) onClose();
        return;
      }
      setSaving("saved");
      setTimeout(() => {
        setSaving("");
        if (close) onClose();
      }, 700);
    } catch (e) {
      setSaving("");
      setSaveError(`Couldn't save: ${messageOf(e)}`);
    }
  };

  /* Done: for Molly, keep what she's changed — the closet's rails, and the
     room as this season's layout on this device — then close */
  const done = () => {
    if (!owner) {
      onClose();
      return;
    }
    void withSaving(async () => {
      let saved = false;
      if (wardrobe) {
        // the rails; where the clothes have been dragged and how big they've been
        // made (− and +); and which have been unhearted
        const stage = stageOf();
        const moves = stage ? closetMovesNow(stage) : undefined;
        const outChanged = hiddenChanged(closetHidden);
        const moved = !!moves && movesChanged(which, moves);
        if (racksChanged(which) || moved || outChanged) {
          await publishRacks(which, moved ? moves : undefined, outChanged ? closetHidden : undefined);
          saved = true;
        }
      }
      if (differs(roomNow(), wearing ?? { stage: { w: 1, h: 1 }, props: {} }, place)) {
        await saveTo(season);
        saved = true;
      }
      return saved;
    }, true);
  };

  /**
   * The room as it is right now, as JSON: what's in it, and for each sticker
   * how far it's been moved from where the layout puts it, its size and its
   * stacking order — the same numbers arrangement.ts keeps, so a copy of this
   * can become the new default.
   */
  const copyLayout = async () => {
    const stage = stageOf();
    if (!stage) return;
    let text: string;
    if (page === "racks") {
      // the order of the clothes on every rail, to become the default in clothes.ts
      text = JSON.stringify({ closet: which, racks: rackOrder }, null, 2);
    } else {
      const { stage: size, layout: kind, season: at, props } = capture(stage, out, clothesPage ? "closet" : "room");
      text = JSON.stringify({ place, stage: size, layout: kind, season: at, props }, null, 2);
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied("ok");
    } catch {
      // older browsers, or no permission: the textarea trick
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      setCopied(document.execCommand("copy") ? "ok" : "fail");
      ta.remove();
    }
    setTimeout(() => setCopied(""), 2200);
  };

  const jump = (key: string) => {
    setTab(key);
    // scroll just the list: scrollIntoView would also scroll the page under it
    const list = panel.current?.querySelector<HTMLElement>(".cat-scroll");
    const shelfEl = panel.current?.querySelector<HTMLElement>(`#cat-shelf-${key}`);
    if (list && shelfEl) list.scrollTo({ top: shelfEl.offsetTop, behavior: "smooth" });
  };

  const pages: [Page, string][] = [
    ...(wardrobe ? ([["clothes", "♡ Clothes"], ["racks", "🪝 Racks"]] as [Page, string][]) : []),
    ["stickers", wardrobe ? "✿ Stickers" : "♡ Stickers"],
    // the wardrobe comes with you: clothes can be worn into any room
    ...(!wardrobe && onRoomClothes ? ([["clothes", "♡ Clothes"]] as [Page, string][]) : []),
    ["seasons", `${SEASON_EMOJI[season]} Seasons`],
    ["looks", "✦ Saved looks"],
    // the visitor book is Molly's alone (the database also only answers editors)
    ...(owner ? ([["visitors", "☆ Visitors"]] as [Page, string][]) : []),
  ];

  const where = `${phone ? "phone" : "computer"}`;
  const saveNote = !collection.configured ? null : owner ? (
    <p>
      <b>Save</b> keeps {wardrobe ? "the rails and " : ""}this room as its {SEASON_EMOJI[season]} {season} layout on a {where}.
    </p>
  ) : (
    <>
      <p>Changes here are only for you, until the page reloads.</p>
      <SignIn editor={collection.editor} onError={setSaveError} label="Molly? Sign in to save the room" />
    </>
  );

  return (
    <div
      className="cat-backdrop"
      style={{
        ["--cat-jewel" as string]: palette.jewel,
        ["--cat-ink" as string]: palette.ink,
        ["--cat-chip" as string]: `url("${floralSrc(chips.now)}")`,
        // the head's own stone for what's selected here, the sidebar's for Save
        ["--cat-jewel-side" as string]: paletteOf(chips.now).jewel,
        ["--cat-ink-side" as string]: paletteOf(chips.now).ink,
        ["--cat-jewel-foot" as string]: paletteOf(ribbon.now).jewel,
      }}
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panel}
        className="cat-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cat-title"
        style={{ backgroundImage: `linear-gradient(rgba(253, 248, 238, 0.965), rgba(250, 243, 229, 0.975)), url("${floralSrc(paper.now)}")` }}
      >
        <header className="cat-head">
          {/* one of Molly's florals, turning slowly, the same as the footer */}
          {floral.was && (
            <div aria-hidden className="palais-floral palais-floral--cover palais-floral--out" style={{ backgroundImage: `url("${floralSrc(floral.was)}")` }} />
          )}
          <div aria-hidden className="palais-floral palais-floral--cover" key={floral.now} style={{ backgroundImage: `url("${floralSrc(floral.now)}")` }} />
          <span aria-hidden className="cat-sparkle cat-sparkle--a">✦</span>
          <span aria-hidden className="cat-sparkle cat-sparkle--b">✧</span>
          <h2 id="cat-title">
            <span aria-hidden>♡ </span>
            {wardrobe ? "Wardrobe Catalogue" : "Palais Catalogue"}
            <span aria-hidden> ♡</span>
          </h2>
          <p className="cat-count">
            {onSkipSeason ? (
              <button
                type="button"
                className="cat-season-now cat-season-now--go"
                onClick={onSkipSeason}
                aria-label={`Skip to ${nextSeason(season)}`}
                title={`On to ${titleCase(nextSeason(season))}`}
              >
                {SEASON_EMOJI[season]} {titleCase(season)} <span aria-hidden>▸</span>
              </button>
            ) : (
              <span className="cat-season-now">
                {SEASON_EMOJI[season]} {titleCase(season)}
              </span>
            )}{" "}
            · {PLACE_NAMES[place]}
            {(clothesPage || page === "stickers") && (
              <>
                {" "}
                · {shown} of {inRoom.length} in the {clothesPage ? "closet" : "room"}
              </>
            )}
          </p>
          <button type="button" className="cat-close" onClick={onClose} aria-label="Close the catalogue">
            ×
          </button>
          <div className="cat-pages" role="tablist" aria-label="Catalogue pages">
            {pages.map(([key, name]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={page === key}
                className={`cat-page${page === key ? " is-on" : ""}`}
                onClick={() => setPage(key)}
              >
                {name}
              </button>
            ))}
          </div>
          <div className={`cat-find${clothesPage || page === "stickers" ? "" : " is-away"}`} aria-hidden={!(clothesPage || page === "stickers")}>
            {page === "seasons" && <span className="cat-find-note">{PLACE_NAMES[place]} has a layout for each season</span>}
            {page === "looks" && <span className="cat-find-note">♡ Save the room as a new look</span>}
            {page === "visitors" && <span className="cat-find-note">☆ Visitor Information</span>}
              <span aria-hidden>🔍</span>
              <input
                type="search"
                value={find}
                placeholder={clothesPage ? "Search the wardrobe…" : "Search the catalogue…"}
                aria-label={clothesPage ? "Search the wardrobe" : "Search the catalogue"}
                onChange={(e) => setFind(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && find && (e.stopPropagation(), setFind(""))}
              />
              {!!words.length && (
                <>
                  <span className="cat-find-count">
                    {hits} {hits === 1 ? "match" : "matches"}
                  </span>
                  <button type="button" onClick={() => setFind("")} aria-label="Clear the search">
                    ×
                  </button>
                </>
              )}
          </div>
          {page === "visitors" && (
            <nav className="cat-tabs cat-tabs--when" aria-label="Time range">
              {VISIT_RANGES.map((r) => (
                <button
                  key={r.days}
                  type="button"
                  className={`cat-tab${visitDays === r.days ? " is-on" : ""}`}
                  aria-pressed={visitDays === r.days}
                  onClick={() => setVisitDays(r.days)}
                >
                  {r.label}
                </button>
              ))}
            </nav>
          )}
          {page === "looks" && (
            <nav className="cat-tabs cat-tabs--when" aria-label="Season">
              {SEASON_NAMES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cat-tab${lookSeason === s ? " is-on" : ""}`}
                  aria-pressed={lookSeason === s}
                  onClick={() => setLookSeason(s)}
                >
                  <span aria-hidden>{SEASON_EMOJI[s]}</span> {titleCase(s)}
                </button>
              ))}
            </nav>
          )}
          {page === "seasons" && (
            <div className="cat-tabs cat-tabs--sign">
              <SignIn editor={collection.editor} onError={setSaveError} label="Sign in" />
            </div>
          )}
          <nav
            className={`cat-tabs${(clothesPage || page === "stickers") && shelves.length ? "" : " is-away"}`}
            aria-label="Shelves"
            aria-hidden={!((clothesPage || page === "stickers") && shelves.length)}
            hidden={page === "visitors" || page === "looks" || page === "seasons"}
          >
              {shelves.map(({ shelf }) => (
                <button
                  key={shelf.key}
                  type="button"
                  className={`cat-tab${tab === shelf.key ? " is-on" : ""}`}
                  onClick={() => jump(shelf.key)}
                >
                  <span aria-hidden>{shelf.emoji}</span> {shelf.name}
                </button>
              ))}
          </nav>
        </header>

        <div className="cat-scroll">
          {page !== "visitors" && page !== "looks" && (saveNote || saveError) && (
            <div className="rack-save-note">
              {saveNote}
              {saveError && <p className="rack-save-error">{saveError}</p>}
            </div>
          )}
          {page === "racks" ? (
            <RacksShelf which={which} hidden={closetHidden} setHidden={setClosetHidden} />
          ) : page === "seasons" ? (
            <SeasonsShelf
              place={place}
              season={season}
              device={device}
              collection={collection}
              wearing={wearing}
              owner={owner}
              busy={saving === "busy"}
              onWear={onWear}
              stickers={stickers}
              hidden={hidden}
              onPutIn={onPutIn}
              onAddTo={(s, d, ids) =>
                withSaving(async () => {
                  // the other device's layout isn't on screen: add to it in the collection
                  const look = defaultLook(collection.saved, place, s, d);
                  const props = { ...look.layout.props };
                  for (const id of ids) {
                    const before = props[id] as (typeof props)[string] | undefined;
                    props[id] = before ? { ...before, shown: true } : { x: 0, y: 0, s: 1, z: 60, shown: true };
                  }
                  await saveSeasonDefault(collection.saved, {
                    place,
                    season: s,
                    device: d,
                    name: `${PLACE_NAMES[place]} · ${titleCase(s)} · ${d === "phone" ? "phone" : "computer"}`,
                    stage: look.layout.stage,
                    props,
                  });
                  await collection.refresh();
                  return true;
                }, false)
              }
              onSaveTo={(s) => {
                if (s === season || window.confirm(`Save the room as it is now as its ${s} layout on a ${where}?`)) {
                  void withSaving(async () => {
                    await saveTo(s);
                    return true;
                  }, false);
                }
              }}
            />
          ) : page === "visitors" ? (
            <VisitorsShelf forDays={visitDays} />
          ) : page === "looks" ? (
            <LayoutsShelf
              collection={collection}
              place={place}
              device={device}
              season={season}
              wearing={wearing}
              onWear={onWear}
              hidden={hidden}
              stageOf={stageOf}
              viewing={lookSeason}
              onViewing={setLookSeason}
            />
          ) : (
            !shelves.length ? (
              <p className="cat-note">
                Nothing here matches “{find.trim()}”.
              </p>
            ) : (
            shelves.map(({ shelf, items }) => {
              const on = items.filter((id) => !out.has(id)).length;
              const all = on === items.length;
              return (
                <section key={shelf.key} id={`cat-shelf-${shelf.key}`} className="cat-shelf">
                  <div className="cat-shelf-head">
                    <ShelfTick
                      all={all}
                      some={on > 0}
                      onChange={() => setMany(items, !all)}
                      label={`${all ? "Take out" : "Put in"} all the ${shelf.name.toLowerCase()}`}
                    />
                    <h3>
                      <span aria-hidden>{shelf.emoji}</span> {shelf.name}
                    </h3>
                    <span className="cat-shelf-count">
                      {on}/{items.length}
                    </span>
                    {sizeGroup(items, `all the ${shelf.name.toLowerCase()}`)}
                  </div>
                  <ul className="cat-grid">
                    {items.map((id) => {
                      const inIt = !out.has(id);
                      const g = clothesPage ? garment(id) : undefined;
                      const name = g ? g.label : nice(id);
                      // who put it in the room, and whether it's theirs to keep (placements.ts)
                      const by = clothesPage || !inIt ? null : addedBy(id);
                      const locked = !clothesPage && isLocked(id);
                      return (
                        <li key={id}>
                          <div className={`cat-card${inIt ? " is-on" : ""}`}>
                            <button
                              type="button"
                              className={`cat-heart cat-heart--card${inIt ? " is-on" : ""}`}
                              aria-pressed={inIt}
                              aria-label={`${inIt ? "Take out" : "Put in"} ${name}`}
                              title={locked ? (by === "mollybeach" ? "Molly put this here" : "Sign in to put things in the rooms") : inIt ? "Take it out" : "Put it in"}
                              disabled={locked}
                              onClick={() => setMany([id], !inIt)}
                            />
                            <span className="cat-size" role="group" aria-label={`Size of ${name}`}>
                              <button type="button" disabled={!inIt || locked} aria-label={`Make ${name} smaller`} title="Smaller" onClick={() => nudge(id, 1 / 1.12)}>
                                −
                              </button>
                              <button type="button" disabled={!inIt || locked} aria-label={`Make ${name} bigger`} title="Bigger" onClick={() => nudge(id, 1.12)}>
                                +
                              </button>
                            </span>
                            <span className="cat-thumb">
                              <img src={clothesPage ? closetSrc(id) : propSrc(id as PropId)} alt="" loading="lazy" decoding="async" />
                            </span>
                            <span className="cat-name">{name}</span>
                            {g && <span className="cat-store">{g.store}</span>}
                            {by && <span className="cat-added">added to scene by {by}</span>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })
            )
          )}
        </div>

        <footer className="cat-foot">
          {page === "racks" && (
            <button
              type="button"
              className="cat-btn cat-btn--ghost"
              onClick={() => resetRacks(which)}
              disabled={!racksChanged(which)}
              title="Put every piece back where it started"
            >
              Put it all back
            </button>
          )}
          {page === "racks" && owner && (
            <button
              type="button"
              className="cat-btn cat-btn--ghost"
              onClick={() => freshFromCode(which)}
              title="Hang everything where the closet itself says it goes — then Save to keep it"
            >
              Lay it out fresh
            </button>
          )}
          {(clothesPage || page === "stickers") && (
            <>
              <span className="cat-size-all">
                Size of {words.length ? "the matches" : "everything"}{" "}
                {sizeGroup(onPage, words.length ? "everything the search found" : clothesPage ? "all the clothes" : "everything in the room")}
              </span>
              <button type="button" className="cat-btn" onClick={() => setMany(onPage, true)}>
                Show {words.length ? "the matches" : "everything"}
              </button>
              <button type="button" className="cat-btn cat-btn--ghost" onClick={() => setMany(onPage, false)}>
                Hide {words.length ? "the matches" : "everything"}
              </button>
            </>
          )}
          <button
            type="button"
            className="cat-btn cat-btn--copy"
            onClick={copyLayout}
            title={page === "racks" ? "Copy the order of the clothes on every rail, to send to Claude" : "Copy what's in the room and where everything is, to send to Claude"}
          >
            {copied === "ok" ? "Copied ♡" : copied === "fail" ? "Couldn't copy" : page === "racks" ? "Copy arrangement" : "Copy layout"}
          </button>
          <button type="button" className="cat-btn cat-btn--done" onClick={done} disabled={saving === "busy"}>
            {saving === "busy" ? "Saving…" : saving === "saved" ? "Saved ♡" : owner ? "Save ♡" : "Done ♡"}
          </button>
        </footer>
      </div>
    </div>
  );
}
